import { FlatRecord } from 'flatrecord/geojson'
import GeoJSON from 'geojson'
import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Result } from '@/domain/protocols/Result'

import { mapAirspaceFeature } from './AirspaceFlatRecordMapper'
import { AirspaceFeature, AirspaceFeatureCollection, IAirspacesRepository } from './IAirspacesRepository'

import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { loadDatasetBytes } from '@/infra/datasetCache/loadDatasetBytes'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import type { ILogger } from '@/infra/logger/ILogger'

const LOG_TAG = 'AirspacesRepository'

// Chave de tipo -> suas features. Construído uma vez no parse a partir do dataset
// FlatRecord plano; getAll/getByCode ficam síncronos sobre estas coleções em memória.
type AirspaceBuckets = Map<string, AirspaceFeatureCollection>

// Ref estável compartilhada para tipos vazios/desconhecidos (bom para deps do
// React). Congelada para que um consumidor nunca mute o singleton compartilhado
// por toda consulta vazia.
const EMPTY_COLLECTION: AirspaceFeatureCollection = Object.freeze({
  type: 'FeatureCollection',
  features: Object.freeze([]) as AirspaceFeature[]
}) as AirspaceFeatureCollection

/**
 * Repositório de espaços aéreos alimentado pelo dataset FlatRecord (`.frb`) do
 * Jetstream. Espelha o `NavaidRepository`: baixa o buffer, drena todas as
 * features uma vez para buckets por tipo (no shape legado do Geoserver) e mantém
 * tudo em memória — o planning-app AGORA persiste este dataset com TTL (ver DatasetCache),
 * rebaixado a cada boot. Isso mantém `IAirspacesRepository` síncrono e esconde o
 * formato FRB de todos os consumidores.
 */
@injectable()
export class JetStreamAirspacesRepository implements IAirspacesRepository {
  private buckets: AirspaceBuckets = new Map()

  constructor(
    @inject(InjectionTokens.HttpClient) private readonly httpClient: IHttpClient,
    @inject(InjectionTokens.Logger) private readonly logger: ILogger,
    @inject(InjectionTokens.DatasetCache) private readonly datasetCache: DatasetCache
  ) {}

  async init(): Promise<Result<void>> {
    return this.load()
  }

  async refresh(): Promise<Result<void>> {
    return this.load()
  }

  getAll(type: string): AirspaceFeatureCollection {
    return this.buckets.get(type) ?? EMPTY_COLLECTION
  }

  getByCode(type: string, code: string): AirspaceFeature | null {
    // Nunca casar em código nullish: features podem vir sem um, e
    // `undefined === undefined` selecionaria um irmão sem código arbitrário.
    if (!code) return null
    const collection = this.buckets.get(type)
    if (!collection) return null
    return collection.features.find((feature) => feature.properties && feature.properties.code === code) ?? null
  }

  getAvailableTypes(): string[] {
    return [...this.buckets.keys()]
  }

  private getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_CDN_BASEURL}/bra/airspaces.frb`
  }

  private async load(): Promise<Result<void>> {
    try {
      // Cache-first: dentro do soft TTL isto resolve sem tocar a rede.
      const loaded = await loadDatasetBytes(
        this.httpClient,
        this.datasetCache,
        'airspaces',
        this.getRemoteUrl(),
        this.logger
      )

      if (!loaded) {
        this.logger.warn(LOG_TAG, 'Could not fetch airspace dataset')
        return Result.fail('Could not fetch airspace dataset')
      }

      const fr = await FlatRecord.open(loaded.bytes)
      if (fr.preload) await fr.preload()
      const features = await fr.loadFeatures()

      const buckets: AirspaceBuckets = new Map()
      for (const feature of features) {
        const mapped = mapAirspaceFeature(feature as GeoJSON.Feature)
        if (!mapped) continue
        let collection = buckets.get(mapped.typeKey)
        if (!collection) {
          collection = { type: 'FeatureCollection', features: [] }
          buckets.set(mapped.typeKey, collection)
        }
        collection.features.push(mapped.feature)
      }

      // Só substitui a base se o parse rendeu algo — preserva os buckets anteriores
      // em download ruim.
      if (buckets.size === 0) {
        this.logger.warn(LOG_TAG, 'Airspace dataset parsed to zero features; keeping previous data')
        return Result.fail('Empty airspace dataset')
      }

      this.buckets = buckets
      this.logger.info(
        LOG_TAG,
        `Loaded ${features.length} airspace features into ${buckets.size} types from FlatRecord`
      )
      return Result.ok()
    } catch (error: any) {
      this.logger.error(LOG_TAG, `Could not load airspace dataset: ${error?.message ?? error}`)
      return Result.fail(error)
    }
  }
}
